import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import './Dashboard.css';
import OfferTable from '../../components/tables/OfferTable/OfferTable';
import ApprovalRequestsTable from '../UserRolesApproval/UserApproval/ApprovalRequests';
import ApprovalTracking from '../UserRolesApproval/UserApproval/ApprovalTracking';
import ApprovalTrackingChart from '../../components/charts/ApprovalTrackingChart/ApprovalTrackingChart';
import OfferBarChart from '../../components/charts/OfferBarChart/OfferBarChart';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../utils/axiosConfig';
import { Link } from 'react-router-dom';

const Dashboard = () => {
    const { user, hasRole } = useAuth();
    const [offerCounts, setOfferCounts] = useState({
        total: 0,
        active: 0,
        pending: 0,
        rejected: 0,
    });
    const [offerStatusData, setOfferStatusData] = useState([]);

    // CMS Manager specific data
    const [vehicleCounts, setVehicleCounts] = useState({ total: 0, pending: 0, approved: 0 });
    const [sparePartsCounts, setSparePartsCounts] = useState({ total: 0, pending: 0, approved: 0 });
    const [maintenanceCounts, setMaintenanceCounts] = useState({ total: 0, pending: 0, approved: 0 });

    const isCmsManager = hasRole('cms_manager');
    const isOfferRole = hasRole(['admin', 'it', 'mr-manager', 'mr-user']);

    useEffect(() => {
        if (isOfferRole) {
            fetchOfferData();
        }
        if (isCmsManager || hasRole(['admin', 'it'])) {
            fetchCmsData();
        }
    }, []);

    const fetchOfferData = async () => {
        try {
            const response = await axiosInstance.get('/offers');
            if (response.data && Array.isArray(response.data.data)) {
                const offers = response.data.data;
                const total = offers.length;
                const active = offers.filter((offer) => offer.status === 'approved').length;
                const pending = offers.filter((offer) => offer.status === 'pending').length;
                const rejected = offers.filter((offer) => offer.status === 'rejected_for_edit' || offer.status === 'rejected').length;

                setOfferCounts({ total, active, pending, rejected });
                setOfferStatusData([
                    { name: 'Approved', value: active, color: '#28a745' },
                    { name: 'Pending', value: pending, color: '#ffc107' },
                    { name: 'Rejected', value: rejected, color: '#dc3545' },
                ]);
            }
        } catch (error) {
            console.error('Error fetching offers:', error);
        }
    };

    const fetchCmsData = async () => {
        try {
            // Fetch vehicles - use public endpoint as fallback
            const vehiclesRes = await axiosInstance.get('/vehicles');
            const vehicles = vehiclesRes.data.data || vehiclesRes.data || [];
            if (Array.isArray(vehicles)) {
                setVehicleCounts({
                    total: vehicles.length,
                    pending: vehicles.filter(v => v.status === 'pending').length,
                    approved: vehicles.filter(v => v.status === 'approved').length,
                });
            }
        } catch (error) {
            console.error('Error fetching vehicles:', error);
        }

        try {
            // Fetch spare parts
            const sparePartsRes = await axiosInstance.get('/spare-parts');
            const spareParts = sparePartsRes.data.data || sparePartsRes.data || [];
            if (Array.isArray(spareParts)) {
                setSparePartsCounts({
                    total: spareParts.length,
                    pending: spareParts.filter(s => s.status === 'pending').length,
                    approved: spareParts.filter(s => s.status === 'approved').length,
                });
            }
        } catch (error) {
            console.error('Error fetching spare parts:', error);
        }

        try {
            // Fetch maintenance
            const maintenanceRes = await axiosInstance.get('/maintenance-calculator');
            const maintenance = maintenanceRes.data.data || maintenanceRes.data || [];
            if (Array.isArray(maintenance)) {
                setMaintenanceCounts({
                    total: maintenance.length,
                    pending: maintenance.filter(m => m.status === 'pending').length,
                    approved: maintenance.filter(m => m.status === 'approved').length,
                });
            }
        } catch (error) {
            console.error('Error fetching maintenance:', error);
        }
    };

    // CMS Manager Dashboard
    if (isCmsManager && !hasRole(['admin', 'it'])) {
        return (
            <Container fluid className="dashboard-page">
                <div className="user-title">
                    <h3>Welcome, {user?.name || 'CMS Manager'}</h3>
                </div>

                {/* Quick Stats */}
                <Row className="mt-4">
                    <Col md={4}>
                        <Card className="mb-3 border-primary">
                            <Card.Body>
                                <Card.Title>
                                    <Link to="/vehicle-management" className="text-decoration-none">Vehicles</Link>
                                </Card.Title>
                                <Row>
                                    <Col>
                                        <h2>{vehicleCounts.total}</h2>
                                        <small className="text-muted">Total</small>
                                    </Col>
                                    <Col>
                                        <h4><Badge bg="warning">{vehicleCounts.pending}</Badge></h4>
                                        <small className="text-muted">Pending</small>
                                    </Col>
                                    <Col>
                                        <h4><Badge bg="success">{vehicleCounts.approved}</Badge></h4>
                                        <small className="text-muted">Approved</small>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="mb-3 border-success">
                            <Card.Body>
                                <Card.Title>
                                    <Link to="/spare-parts-management/spare-parts-pricing" className="text-decoration-none">Spare Parts</Link>
                                </Card.Title>
                                <Row>
                                    <Col>
                                        <h2>{sparePartsCounts.total}</h2>
                                        <small className="text-muted">Total</small>
                                    </Col>
                                    <Col>
                                        <h4><Badge bg="warning">{sparePartsCounts.pending}</Badge></h4>
                                        <small className="text-muted">Pending</small>
                                    </Col>
                                    <Col>
                                        <h4><Badge bg="success">{sparePartsCounts.approved}</Badge></h4>
                                        <small className="text-muted">Approved</small>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="mb-3 border-info">
                            <Card.Body>
                                <Card.Title>
                                    <Link to="/cms/maintenance-calculator" className="text-decoration-none">Maintenance Calculator</Link>
                                </Card.Title>
                                <Row>
                                    <Col>
                                        <h2>{maintenanceCounts.total}</h2>
                                        <small className="text-muted">Total</small>
                                    </Col>
                                    <Col>
                                        <h4><Badge bg="warning">{maintenanceCounts.pending}</Badge></h4>
                                        <small className="text-muted">Pending</small>
                                    </Col>
                                    <Col>
                                        <h4><Badge bg="success">{maintenanceCounts.approved}</Badge></h4>
                                        <small className="text-muted">Approved</small>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Quick Links */}
                <Row className="mt-4">
                    <Col md={12}>
                        <Card>
                            <Card.Header>Quick Actions</Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={3}>
                                        <Link to="/vehicle-management" className="btn btn-outline-primary w-100 mb-2">
                                            View All Vehicles
                                        </Link>
                                    </Col>
                                    <Col md={3}>
                                        <Link to="/spare-parts-management/spare-parts-pricing" className="btn btn-outline-success w-100 mb-2">
                                            Manage Spare Parts
                                        </Link>
                                    </Col>
                                    <Col md={3}>
                                        <Link to="/cms/maintenance-calculator" className="btn btn-outline-info w-100 mb-2">
                                            Maintenance Calculator
                                        </Link>
                                    </Col>
                                    <Col md={3}>
                                        <Link to="/cms/terms-conditions" className="btn btn-outline-secondary w-100 mb-2">
                                            Terms & Conditions
                                        </Link>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        );
    }

    // Default Dashboard (Offers focused)
    return (
        <Container fluid className="dashboard-page">
            <div className="user-title">
                <h3>Good Day, {user?.name || 'Guest'}</h3>
            </div>

            {/* CMS Stats for Admin/IT */}
            {hasRole(['admin', 'it']) && (
                <Row className="mt-4">
                    <Col md={4}>
                        <Card className="mb-3 border-primary">
                            <Card.Body className="py-2">
                                <small className="text-muted">Vehicles</small>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0">{vehicleCounts.total}</h4>
                                    <Badge bg="warning">{vehicleCounts.pending} pending</Badge>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="mb-3 border-success">
                            <Card.Body className="py-2">
                                <small className="text-muted">Spare Parts</small>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0">{sparePartsCounts.total}</h4>
                                    <Badge bg="warning">{sparePartsCounts.pending} pending</Badge>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="mb-3 border-info">
                            <Card.Body className="py-2">
                                <small className="text-muted">Maintenance</small>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0">{maintenanceCounts.total}</h4>
                                    <Badge bg="warning">{maintenanceCounts.pending} pending</Badge>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Offer Management */}
            {isOfferRole && (
                <>
                    <Row className="mt-4">
                        <Col md={3}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title>Total Offers</Card.Title>
                                    <h3>{offerCounts.total}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title>Active Offers</Card.Title>
                                    <h3>{offerCounts.active}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title>Pending Offers</Card.Title>
                                    <h3>{offerCounts.pending}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title>Rejected Offers</Card.Title>
                                    <h3>{offerCounts.rejected}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Row className="mt-4">
                        <Col md={4}>
                            <Card className="mb-4">
                                <Card.Body>
                                    <Card.Title>Offer Status Breakdown</Card.Title>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={offerStatusData}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={100}
                                                label
                                            >
                                                {offerStatusData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Card.Body>
                            </Card>

                            <Card>
                                <Card.Body>
                                    <Card.Title>Offer Count Overview</Card.Title>
                                    <OfferBarChart offerCounts={offerCounts} />
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={8}>
                            <Card>
                                <Card.Body>
                                    <Card.Title>Current Offers</Card.Title>
                                    <OfferTable />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Approval Tracking */}
                    <Row className="mt-4">
                        {!hasRole('mr-user') && (
                            <Col md={12}>
                                <Card>
                                    <Card.Body>
                                        <ApprovalRequestsTable />
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                        {!hasRole('mr-user') && (
                            <Col md={3}>
                                <Card>
                                    <Card.Body>
                                        <ApprovalTrackingChart />
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}

                        <Col md={hasRole('mr-user') ? 12 : 9}>
                            <Card>
                                <Card.Body>
                                    <ApprovalTracking />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </Container>
    );
};

export default Dashboard;
